import React from 'react';
import { FileSpreadsheet, ArrowRight, Cpu, Package, Network, CheckSquare } from 'lucide-react';
import { useDbNavigate } from '../../context/DatabaseContext';

const reportGroups = [
  {
    id: 'general',
    label: 'General',
    icon: Cpu,
    reports: [
      {
        slug: 'io-assignments',
        label: 'I/O Assignments',
        description: 'Lists every tag\'s I/O assignment details — panel, drop, slot, and channel. Used to verify wiring assignments and cross-reference physical panel layout against the tag database.'
      },
      {
        slug: 'drawing-log',
        label: 'Drawing Log',
        description: 'Shows the full revision history for drawings, filterable by source and drawing name. Used to track when drawings were revised, what changed, and when they were last plotted.'
      },
      {
        slug: 'panel-report',
        label: 'Panel Report',
        description: 'Groups tag I/O assignments by panel, drop, slot, and channel. Organized around the panel hierarchy rather than the tag list — useful for panel-by-panel field verification.'
      }
    ]
  },
  {
    id: 'submittals',
    label: 'Submittals',
    icon: Package,
    reports: [
      {
        slug: 'index-panels',
        label: 'Index Report - Panels',
        description: 'Outputs index sheet data organized by panel. Used to generate the panel index pages that appear at the front of drawing packages, listing all panels and their contents.'
      },
      {
        slug: 'index-tags',
        label: 'Index Report - Tags',
        description: 'Outputs index sheet data organized by tag. Used to generate the tag index pages in drawing packages, listing all instrumentation tags and associated drawing references.'
      }
    ]
  },
  {
    id: 'networking',
    label: 'Networking',
    icon: Network,
    reports: [
      {
        slug: 'ip-addresses-dln',
        label: 'IP Addresses - DLN',
        description: 'Lists all IP address assignments on the Dynamic LAN (DLN). Used to audit network addressing, identify conflicts, and document device IP allocations on the unsecured network segment.'
      },
      {
        slug: 'ip-addresses-sln',
        label: 'IP Addresses - SLN',
        description: 'Lists all IP address assignments on the Secure LAN (SLN). Used to audit network addressing and document device IP allocations on the secure/isolated control network segment.'
      },
      {
        slug: 'network-connection',
        label: 'Network Connection Report',
        description: 'Shows ENET network connections by equipment, panel, and port. Used to document physical Ethernet topology — what device is plugged into which switch port on which panel.'
      }
    ]
  },
  {
    id: 'commissioning',
    label: 'Commissioning',
    icon: CheckSquare,
    reports: [
      {
        slug: 'oat-report',
        label: 'OAT Report',
        description: 'Shows Owner Acceptance Testing (OAT) status for each tag. Used during commissioning handoff to track which tags have been witnessed and accepted by the owner.'
      },
      {
        slug: 'qaqc-report',
        label: 'QAQC Report',
        description: 'Shows construction QA/QC status by tag. Used during the construction phase to track field verification — loop checks, continuity, instrument calibration, etc.'
      },
      {
        slug: 'commissioning-status',
        label: 'Commissioning Status Report',
        description: 'Combined view of construction QA/QC and OAT status for every tag. The single-source-of-truth report for overall commissioning progress across both phases.'
      }
    ]
  }
];

const ReportsPage = () => {
  const navigate = useDbNavigate();

  const totalReports = reportGroups.reduce((sum, g) => sum + g.reports.length, 0);

  return (
    <div className="page-container" style={{ padding: '20px' }}>
      <div style={{
        display: 'flex', alignItems: 'center',
        padding: 'var(--spacing-sm) var(--spacing-md)',
        backgroundColor: 'var(--color-bg-white)',
        borderBottom: '1px solid var(--color-gray-200)',
        marginBottom: 'var(--spacing-lg)'
      }}>
        <FileSpreadsheet size={20} style={{ marginRight: '8px', color: 'var(--color-primary)' }} />
        <h1 style={{ margin: 0, fontSize: 'var(--text-lg)', color: 'var(--color-primary)' }}>
          Reports
        </h1>
        <span style={{ marginLeft: '12px', fontSize: 'var(--text-sm)', color: 'var(--color-gray-500)' }}>
          {totalReports} reports available
        </span>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-xl)' }}>
        {reportGroups.map(group => {
          const GroupIcon = group.icon;
          return (
            <div key={group.id}>
              {/* Group header */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                marginBottom: 'var(--spacing-md)',
                paddingBottom: 'var(--spacing-sm)',
                borderBottom: '2px solid var(--color-primary)'
              }}>
                <GroupIcon size={16} style={{ color: 'var(--color-primary)' }} />
                <span style={{
                  fontSize: 'var(--text-base)',
                  fontWeight: 700,
                  color: 'var(--color-primary)',
                  fontFamily: "'Rajdhani', 'Roboto Condensed', sans-serif",
                  textTransform: 'uppercase',
                  letterSpacing: '1px'
                }}>
                  {group.label}
                </span>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-gray-400)' }}>
                  {group.reports.length} {group.reports.length === 1 ? 'report' : 'reports'}
                </span>
              </div>

              {/* Report cards */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
                gap: 'var(--spacing-md)'
              }}>
                {group.reports.map(report => (
                  <div
                    key={report.slug}
                    onClick={() => navigate(`/reports/${report.slug}`)}
                    style={{
                      background: 'var(--color-bg-white)',
                      border: '1px solid var(--color-gray-200)',
                      borderRadius: '6px',
                      padding: 'var(--spacing-md) var(--spacing-lg)',
                      cursor: 'pointer',
                      transition: 'border-color 0.15s, box-shadow 0.15s',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '8px'
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.borderColor = 'var(--color-primary)';
                      e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.borderColor = 'var(--color-gray-200)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{
                        fontSize: 'var(--text-base)',
                        fontWeight: 600,
                        color: 'var(--color-primary)',
                        fontFamily: "'Rajdhani', 'Roboto Condensed', sans-serif",
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        {report.label}
                      </span>
                      <ArrowRight size={16} style={{ color: 'var(--color-gray-400)', flexShrink: 0 }} />
                    </div>
                    <p style={{
                      margin: 0,
                      fontSize: 'var(--text-sm)',
                      color: 'var(--color-gray-600)',
                      lineHeight: 1.5
                    }}>
                      {report.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReportsPage;
